<template>
    <alert-modal
        @close="close"
        class="cz2__product-info"
    >
        <custom-swiper
            :show-mask="false"
            :no-padding="true"
            :space-between="0"
            :has-mobile-bullets="true"
        >
            <template v-slot:slides>
                <img
                    v-for="image in product.images.medium"
                    :key="image.url"
                    :src="image.url"
                    :alt="image.alt"
                    height="219"
                    width="auto"
                    class="swiper-slide"
                >
            </template>
        </custom-swiper>

        <div class="cz2__modal-popup__body-content">
            <h1>
                <div class="cz2__modal-popup__super-header">
                    {{ product.license }}
                </div>

                <div class="cz2__modal-popup__header">
                    {{ product.productName }}
                </div>
            </h1>

            <div class="cz2__modal-popup__copy cz2__product-price--wrapper">
                <product-flag />

                <p class="cz2__product-price">
                    {{ product.price.sales.formatted }}
                </p>
            </div>

            <span class="cz2__sr-only">
                {{ $t('SR_ACTIVATING') }}
            </span>

            <btn
                v-if="!product.selected"
                @click.native.stop="addProduct(product)"
                class="cz2__checkout__sticker__slide__button cz2__stationary-text-transform"
            >
                {{ $t('ACTION_ADD_TO_CART') }}
            </btn>

            <div
                v-else
                @click.stop
                class="cz2__quantity cz2__font-inter-black"
            >
                <button
                    class="cz2__quantity__sub"
                    @click.stop="quantitySubtract(product)"
                    :aria-label="product.quantity ? $t('QUANTITY_MINUS') : $t('QUANTITY_REMOVE')"
                    type="button"
                    v-html="$t('QUANTITY_MINUS_SYMBOL')"
                />

                <span class="cz2__quantity__count">
                    {{ product.quantity }}
                </span>
                <span class="cz2__sr-only">
                    {{ getProductDescription(product.productName) }}
                </span>

                <button
                    class="cz2__quantity__add"
                    :disabled="!product.canAdd"
                    @click.stop="quantityAdd(product)"
                    :aria-label="$t('QUANTITY_PLUS')"
                    type="button"
                    v-html="$t('QUANTITY_PLUS_SYMBOL')"
                />
            </div>
        </div>
    </alert-modal>
</template>

<style lang="scss">
    @import "../style/variables.scss";

    #cz2.cz2 {
        .cz2__product-info {
            .cz2__modal-popup__body-content {
                margin-top: 32px;
            }

            .cz2__product-price,
            .cz2__modal-popup__copy {
                color: #040504;
                font-family: 'Open Sans', sans-serif;
                line-height: 20px;
            }

            .cz2__modal-popup__super-header,
            .cz2__modal-popup__header,
            .cz2__product-price,
            .cz2__modal-popup__copy {
                text-align: left;
            }

            .cz2__product-price {
                font-size: 24px;
                text-transform: uppercase;
            }

            .cz2__product-price--wrapper {
                margin: 8px 0 16px 0;

                display: flex;
                flex-direction: row;
                align-items: center;
            }

            .cz2__btn {
                margin-top: 16px;
                margin-bottom: 8px;
            }

            .cz2__quantity {
                display: flex;
                align-items: center;
            }

            .cz2__quantity__sub,
            .cz2__quantity__count,
            .cz2__quantity__add {
                color: white;
            }

            .cz2__quantity__count {
                font-size: 20px;
            }

            .cz2__quantity {
                border: 3px solid black;

                background: black;
                height: 40px;

                display: flex;
                justify-content: space-between;
            }
        }
    }
</style>

<script>
    import AlertModal from './ui/AlertModal.vue';
    import CustomSwiper from './ui/CustomSwiper.vue';
    import Btn from './ui/Btn.vue';
    import ProductFlag from './ui/ProductFlag.vue';

    import * as constants from '../constants';

    export default {
        components: {
            AlertModal,
            CustomSwiper,
            Btn,
            ProductFlag,
        },

        computed: {
            productInfoModalComponent() {
                return this.$store.state.productInfoModalComponent;
            },

            product() {
                const record = { ...this.productInfoModalComponent };
                const selected = this.findSelected(record.id);

                if (selected) {
                    record.selected = true;
                    record.quantity = selected.quantity;
                } else {
                    record.selected = false;
                    record.quantity = 0;
                }

                record.inventory = this.quantityLimit(record.ats);
                record.canAdd = record.quantity < record.inventory;

                return record;
            },

            selectedRecommendations() {
                return this.$store.state.customizer.selectedRecommendations;
            },

            viewController() {
                return this.$store.state.customizer.viewController;
            },
        },

        methods: {
            addProduct(product) {
                this.$store.dispatch('log', {
                    level: 'info',
                    message: 'Add product',
                });

                this.$store.dispatch('log', {
                    level: 'info',
                    message: `Select product ${product.id}`,
                });

                const selected = this.selectedRecommendations.find((x) => x.id === product.id);
                if (selected == null) {
                    const add = {
                        id: product.id,
                        quantity: 1,
                    };

                    this.$store.dispatch('setSelectedRecommendations', [...this.selectedRecommendations, add]);
                    this.addProductToCart(product);
                } else {
                    const add = { ...selected, quantity: 1 };

                    this.$store.dispatch('setSelectedRecommendations', [...this.selectedRecommendations.filter((x) => x.id !== product.id), add]);
                    this.addProductToCart(product);
                }
            },

            addProductToCart(product) {
                for (let i = 1; i < 20; i += 1) {
                    // Find empty placement.
                    const placementCode = `${constants.PLACEMENT_PRODUCTS}${i}`;
                    const selected = this.viewController.selectedAtPlacement(placementCode);

                    if (selected?.placeholder) {
                        const placement = this.viewController.findPlacement(placementCode);
                        // Use this one.
                        const realProduct = placement.components.find((x) => !x.placeholder);

                        this.viewController.updateComponent(placementCode, realProduct.code);

                        let price = null;

                        if (product.price?.sales?.value) {
                            price = product.price.sales.value;
                        }

                        const image = this.getImage(product);

                        this.viewController.commitCustomAttributes(placementCode, {
                            'fun-sku': product.id,
                            'fun-name': product.productName,
                            'fun-price': price ? price.toString() : '',
                            'fun-image': image,
                        });

                        this.$store.dispatch('setComponentQuantity', {
                            component: realProduct,
                            quantity: 1,
                        });

                        break;
                    }
                }
            },

            quantitySubtract(product) {
                if (product.quantity > 0) {
                    const selected = this.findSelected(product.id);

                    if (selected && selected.quantity > 0) {
                        const add = { ...selected, quantity: selected.quantity - 1 };

                        this.$store.dispatch('setSelectedRecommendations', [...this.selectedRecommendations.filter((x) => x.id !== product.id), add]);
                    }
                }
            },

            quantityAdd(product) {
                if (product.quantity < product.inventory) {
                    const selected = this.findSelected(product.id);

                    if (selected) {
                        const add = { ...selected, quantity: selected.quantity + 1 };

                        this.$store.dispatch('setSelectedRecommendations', [...this.selectedRecommendations.filter((x) => x.id !== product.id), add]);
                    }
                }
            },

            findSelected(id) {
                if (this.selectedRecommendations == null) {
                    return null;
                }

                // eslint-disable-next-line no-restricted-syntax
                for (const s of this.selectedRecommendations) {
                    if (s.id === id && s.quantity > 0) {
                        return s;
                    }
                }

                return null;
            },

            quantityLimit(ats) {
                const settings = this.$store.state.customizer.globalSettings;
                const recommendationLimit = +settings.blueprint.custom['fun-recommendation-limit'];
                let limit = recommendationLimit;

                if (ats > 1 && ats < recommendationLimit) {
                    limit = ats;
                }

                return limit;
            },

            getProductDescription(name) {
                return this.$t('TOTAL_ADDED').replace('{0}', name);
            },

            getImage(product) {
                let image = product?.images?.small[0]?.url;

                if (image == null) {
                    image = product?.images?.medium[0]?.url;
                }

                return image;
            },

            close() {
                this.$store.dispatch('setProductInfoModalComponent', null);
                this.$store.dispatch('showProductInfoModal', false);
            },
        },
    };
</script>
